import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EmbeddedContainer, WidgetContainer, FloatingCircle, HeaderButtons } from './styles';
import Toolbar from 'components/Toolbar';
import { CommonButton } from 'components/Buttons';
import { ReactComponent as RobotSmallIcon } from './svg/robot_small.svg';
import ChatbotInterface from './Components/ChatbotInterface';
import useLoadingMessages from './hooks/useLoadingMessages';
import useDraggable from './hooks/useDraggable';
import chatbotActions from 'state/chatbot/actions';
import selectors from 'state/selectors';
import useChatbot from 'hooks/useChatbot';
import utils from 'utils';

const Chatbot = () => {
  const { messages, loading, clearMessages, abort } = useChatbot();

  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();

  const isMenuOpen = useSelector(selectors.app.getMenu);

  const isMinimized = useSelector(selectors.chatbot.getIsMinimized) || isMenuOpen;
  const displayMode = useSelector(selectors.chatbot.getDisplayMode);
  const [dimensions, setDimensions] = useState({ width: 465, height: 600 });
  const [resizing, setResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    left: 0,
  });
  const { loadingMessage, clearLoadingState } = useLoadingMessages({ loading });
  const { position, setPosition, dragging, startDrag } = useDraggable();

  const handleClearMessages = () => {
    clearLoadingState();
    clearMessages();
    abort();
  };

  const startResize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: dimensions.width,
      height: dimensions.height,
      top: position.y,
      left: position.x,
    });
  };

  const doResize = useCallback(
    (e: MouseEvent) => {
      if (!resizing) return;

      // Prevent text selection during resize
      e.preventDefault();
      e.stopPropagation();

      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      let newWidth = dimensions.width;
      let newHeight = dimensions.height;

      // Top-left corner resize - keep the window position fixed
      // When dragging right: width decreases
      // When dragging down: height decreases
      newWidth = Math.max(350, Math.min(1000, resizeStart.width - deltaX));
      newHeight = Math.max(400, Math.min(1000, resizeStart.height - deltaY));

      const mainElement = document.querySelector('main');
      if (mainElement) {
        mainElement.classList.add('no-select');
      }

      // Get the main element to check its width
      if (mainElement) {
        const mainWidth = mainElement.clientWidth;
        const mainHeight = mainElement.clientHeight;
        const maxWidth = mainWidth * 0.5; // 50% of main element width
        const maxHeight = mainHeight - 70; // main height minus 70px

        // Limit the chatbot width to 50% of main area and height to main height minus 70px
        newWidth = Math.min(newWidth, maxWidth);
        newHeight = Math.min(newHeight, maxHeight);
      }

      setDimensions({ width: newWidth, height: newHeight });
    },
    [dimensions, resizeStart, resizing],
  );

  const stopResize = useCallback(() => {
    setResizing(false);
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.classList.remove('no-select');
    }
  }, []);

  useEffect(() => {
    if (resizing) {
      window.addEventListener('mousemove', doResize);
      window.addEventListener('mouseup', stopResize);
      return () => {
        window.removeEventListener('mousemove', doResize);
        window.removeEventListener('mouseup', stopResize);
      };
    }
  }, [doResize, resizing, stopResize]);

  // Reset widget to initial position and size
  const resetWidget = useCallback(() => {
    // Reset position to bottom-right corner (initial position)
    // For a widget positioned at bottom-right, we want x=0, y=0 (relative to bottom-right)
    setPosition({ x: 0, y: 0 });

    // Reset dimensions to initial size
    setDimensions({ width: 465, height: 600 });
  }, [setPosition, setDimensions]);

  // Check if widget is in initial state
  const isInitialPosition = position.x === 0 && position.y === 0;
  const isInitialSize = dimensions.width === 465 && dimensions.height === 600;
  const isInitialState = isInitialPosition && isInitialSize;

  if (displayMode === 'floating' && !isMinimized) {
    return (
      <>
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            zIndex: 1000,
            width: `${dimensions.width}px`,
            height: `${dimensions.height}px`,
            transform: `translate(${position.x}px, ${position.y}px)`,
            cursor: dragging ? 'grabbing' : resizing ? 'nw-resize' : 'default',
            boxShadow: dragging ? '0 8px 32px rgba(0, 0, 0, 0.2)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
            transition: dragging ? 'none' : 'box-shadow 0.2s ease',
            borderRadius: '8px',
          }}
          data-html2canvas-ignore
        >
          <WidgetContainer>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <ChatbotInterface
                loading={loading}
                loadingMessage={loadingMessage}
                onHeaderMouseDown={startDrag}
                startResize={startResize}
                resetWidget={resetWidget}
                isInitialState={isInitialState}
              />
            </div>
          </WidgetContainer>
        </div>
      </>
    );
  }

  if (displayMode === 'embedded') {
    return (
      <EmbeddedContainer>
        <Toolbar.BlueBar>
          <Toolbar.Left />
          <Toolbar.Right>
            <Toolbar.ModuleName>{utils.app.textTranslated('aiHelper', t, 'AI Helper')}</Toolbar.ModuleName>
          </Toolbar.Right>
        </Toolbar.BlueBar>
        <Toolbar.ActionBar>
          <Toolbar.ButtonsContainer>
            <HeaderButtons>
              <CommonButton
                id="clear-messages"
                action={handleClearMessages}
                scope="tertiary"
                value={utils.app.textTranslated('clearMessages', t, 'Clear')}
                customIcon="clear"
                useThrottle={false}
                disabled={messages.length === 0}
              />
              <CommonButton
                id="dock-ai-helper"
                action={() => dispatch(chatbotActions.setDisplayMode('floating'))}
                scope="tertiary"
                value={utils.app.textTranslated('dockAiHelper', t, 'Dock AI helper')}
                customIcon="dock"
              />
            </HeaderButtons>
          </Toolbar.ButtonsContainer>
        </Toolbar.ActionBar>
        <div
          style={{
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'auto', // Abilita lo scrolling per il contenuto interno
          }}
        >
          <ChatbotInterface loading={loading} loadingMessage={loadingMessage} startResize={startResize} />
        </div>
      </EmbeddedContainer>
    );
  }

  if (isMinimized && displayMode === 'floating') {
    return (
      <FloatingCircle
        onClick={() => {
          dispatch(chatbotActions.setMinimized(false));
          dispatch(chatbotActions.setDisplayMode('floating'));
        }}
        title={utils.app.textTranslated('openChatbot', t, 'Open Chatbot')}
      >
        <RobotSmallIcon width={30} height={30} />
      </FloatingCircle>
    );
  }

  return null;
};

export default Chatbot;
